/**
 * The open gradient section, as a value.
 *
 * The section belongs to the selection it was opened for. It stays open after the gradient is sent, so
 * the same selection can be adjusted and sent again, and it closes when the operator picks a different
 * set of pixels — at which point the next one starts fresh.
 */
export interface GradientSession {
  readonly open: boolean;
  /** The selection the open section belongs to; empty while closed. */
  readonly selection: string;
  /** Whether this selection has had the gradient sent at least once. */
  readonly applied: boolean;
}

export const CLOSED_GRADIENT: GradientSession = { open: false, selection: '', applied: false };

/**
 * The identity of a selection: the SET of selected element keys. Sorted, because the same pixels picked
 * in a different order are the same selection, and a set that loses or gains one pixel is a new one.
 */
export function gradientSelectionId(keys: Iterable<string>): string {
  return [...keys].sort().join('|');
}

/** The entry control: open the section for this selection, or close the one that is open. */
export function toggleGradientSession(session: GradientSession, selection: string): GradientSession {
  return session.open ? CLOSED_GRADIENT : { open: true, selection, applied: false };
}

export function gradientApplied(session: GradientSession): GradientSession {
  return session.open && !session.applied ? { ...session, applied: true } : session;
}

/** A new selection closes the section; the same selection leaves it exactly as it stands. */
export function gradientSessionForSelection(session: GradientSession, selection: string): GradientSession {
  return session.open && session.selection !== selection ? CLOSED_GRADIENT : session;
}

export function gradientEntryLabel(session: GradientSession): string {
  return session.open ? 'Close gradient' : 'Create gradient';
}

export function gradientActionLabel(applied: boolean, busy = false): string {
  if (busy) return 'Applying…';
  return applied ? 'Reapply gradient' : 'Apply gradient';
}
